import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { NotificationToast } from "./NotificationToast";
import {
  selectNotifications,
  removeNotification,
  addNotification,
} from "../store/notificationSlice";
import type { NotificationKind } from "../store/notificationSlice";
import { selectAuth } from "../store/authSlice";
import { selectRewriteSlots } from "../store/rewriteSlotSlice";
import {
  selectReportsJobs,
  selectAllDocumentJobs,
} from "../store/jobSlice";
import type {
  DocumentJobType,
  JobInfo,
  JobStatus,
  ReportsJobType,
} from "../store/jobSlice";
import type { RootState } from "../store";
import AppStatusTray from "./AppStatusTray";

const SOUND_ENABLED_KEY = "notification_sound_enabled";

/** Время жизни тоста по типу, если отправитель не задал ttl. */
const DEFAULT_TTL: Record<NotificationKind, number> = {
  success: 4000,
  info: 5000,
  warning: 7000,
  error: 10000,
};

const documentJobText: Record<
  DocumentJobType,
  { done: string; failed: string }
> = {
  parse: { done: "Документ готов к работе", failed: "Не удалось разобрать документ" },
  paraphrase: { done: "Рерайт готов", failed: "Рерайт не удался" },
  attachReport: { done: "Отчёт прикреплён", failed: "Не удалось прикрепить отчёт" },
};

const reportsJobText: Partial<
  Record<ReportsJobType, { done: string; failed: string }>
> = {
  antiplagiat: { done: "Проверка на антиплагиат завершена", failed: "Проверка на антиплагиат не удалась" },
  attachAntiplagiatReport: { done: "Отчёт антиплагиата прикреплён", failed: "Не удалось прикрепить отчёт антиплагиата" },
};

const isUserCancel = (error: string | null | undefined) =>
  !!error && /отмен|cancel/i.test(error);

const NotificationCenter: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const notifications = useSelector(selectNotifications);
  const reportsJobs = useSelector(selectReportsJobs);
  const documentJobs = useSelector(selectAllDocumentJobs);
  const rewriteSlots = useSelector(selectRewriteSlots);
  const currentSlotId = useSelector(
    (s: RootState) => s.rewriteSlots.currentSlotId
  );
  const auth = useSelector(selectAuth);

  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem(SOUND_ENABLED_KEY);
    return stored === null ? true : stored === "true";
  });

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(SOUND_ENABLED_KEY, String(next));
      return next;
    });
  }, []);

  // Для document-очередей ключ = `${type}:${slotId}`; для reports = type.
  const lastJobStateRef = useRef<
    Record<string, { jobId: string | null; status: JobStatus }>
  >({});
  const playedRevisions = useRef<Map<string, number>>(new Map());
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Контекст, который читают обработчики задач в момент события.
  const viewRef = useRef({ pathname: location.pathname, currentSlotId });
  viewRef.current = { pathname: location.pathname, currentSlotId };
  const slotTitlesRef = useRef<Record<string, string>>({});
  slotTitlesRef.current = useMemo(() => {
    const map: Record<string, string> = {};
    (Array.isArray(rewriteSlots) ? rewriteSlots : []).forEach((s) => {
      if (s.title && s.title.trim()) map[s.id] = s.title.trim();
    });
    return map;
  }, [rewriteSlots]);

  /** Пользователь прямо сейчас видит этот документ — результат и так на экране. */
  const isViewingSlot = useCallback((slotId: string) => {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return false;
    }
    const { pathname, currentSlotId: current } = viewRef.current;
    if (pathname === `/app/${slotId}`) return true;
    return pathname === "/app" && current === slotId;
  }, []);

  const playNotificationSound = useCallback((frequency = 880) => {
    if (!soundEnabled) return;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      // Один контекст на всё приложение: браузеры ограничивают их число.
      const audioCtx: AudioContext = audioCtxRef.current ?? new Ctx();
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === "suspended") void audioCtx.resume();
      const oscillator = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.2, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.25);
      oscillator.connect(gain);
      gain.connect(audioCtx.destination);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.3);
    } catch {
      // игнорируем если аудио недоступно (например, SSR)
    }
  }, [soundEnabled]);

  useEffect(() => {
    return () => {
      void audioCtxRef.current?.close();
    };
  }, []);

  // Джобы: сообщаем только о результате (старт пользователь видит сам) и
  // только если результат не на экране — документ другого слота, другая
  // страница или вкладка в фоне. prepareAntiplagiatDoc — фоновая операция.
  useEffect(() => {
    const remember = (key: string, job: JobInfo) => {
      lastJobStateRef.current[key] = { jobId: job.jobId, status: job.status };
    };
    const hasChanged = (key: string, job: JobInfo) => {
      if (!job.jobId) return false;
      const prev = lastJobStateRef.current[key];
      return !prev || prev.jobId !== job.jobId || prev.status !== job.status;
    };

    (Object.keys(reportsJobText) as ReportsJobType[]).forEach((type) => {
      const job = reportsJobs[type];
      const key = type;
      if (!hasChanged(key, job)) return;
      remember(key, job);
      const text = reportsJobText[type]!;
      if (job.status === "succeeded") {
        dispatch(addNotification({ key: `job:${key}`, type: "success", title: text.done, message: "" }));
      } else if (job.status === "failed" && job.error && !isUserCancel(job.error)) {
        dispatch(addNotification({ key: `job:${key}`, type: "error", title: text.failed, message: job.error }));
      }
    });

    const DOC_TYPES: DocumentJobType[] = ["parse", "paraphrase", "attachReport"];
    Object.entries(documentJobs).forEach(([slotId, bundle]) => {
      DOC_TYPES.forEach((type) => {
        const job = bundle[type];
        const key = `${type}:${slotId}`;
        if (!hasChanged(key, job)) return;
        remember(key, job);
        if (job.status !== "succeeded" && job.status !== "failed") return;
        if (job.status === "failed" && (!job.error || isUserCancel(job.error))) return;

        const viewing = isViewingSlot(slotId);
        // Ошибки разбора и прикрепления отчёта открытый документ показывает
        // сам (экран ошибки / сообщение в панели), у рерайта такого места нет.
        if (viewing && (job.status === "succeeded" || type !== "paraphrase")) return;

        const title = slotTitlesRef.current[slotId];
        const docLabel = title ? `«${title}»` : "Документ";
        const text = documentJobText[type];
        dispatch(
          addNotification({
            key: `job:${key}`,
            type: job.status === "succeeded" ? "success" : "error",
            title: job.status === "succeeded" ? text.done : text.failed,
            message: job.status === "succeeded" ? docLabel : `${docLabel}: ${job.error}`,
            action: viewing ? undefined : { label: "Открыть", to: `/app/${slotId}` },
          })
        );
      });
    });
  }, [reportsJobs, documentJobs, dispatch, isViewingSlot]);

  // Звук — для новых и повторённых уведомлений, кроме информационных.
  useEffect(() => {
    const known = playedRevisions.current;
    const fresh = notifications.filter((n) => known.get(n.id) !== n.revision);
    notifications.forEach((n) => known.set(n.id, n.revision));
    fresh
      .filter((n) => n.type !== "info")
      .forEach((n, idx) => {
        // небольшое смещение, чтобы не наслаивались
        setTimeout(() => playNotificationSound(760 + idx * 40), idx * 80);
      });
  }, [notifications, playNotificationSound]);

  const handleClose = useCallback(
    (id: string) => {
      playedRevisions.current.delete(id);
      dispatch(removeNotification(id));
    },
    [dispatch]
  );

  // Новые — внизу, ближе к лотку статуса.
  const sortedNotifications = useMemo(
    () => [...notifications].sort((a, b) => a.createdAt - b.createdAt),
    [notifications]
  );

  return (
    <div className="notification-center" aria-label="Уведомления">
      {sortedNotifications.map((n) => (
        <NotificationToast
          key={n.id}
          type={n.type}
          title={n.title}
          message={n.message}
          duration={n.ttl ?? DEFAULT_TTL[n.type]}
          revision={n.revision}
          action={
            n.action
              ? { label: n.action.label, onClick: () => navigate(n.action!.to) }
              : undefined
          }
          onClose={() => handleClose(n.id)}
        />
      ))}
      {/* Лоток статуса (соединение + звук) — только в личном кабинете.
          На лендинге и страницах авторизации не отображается. */}
      {auth.isAuthenticated && (
        <AppStatusTray
          soundEnabled={soundEnabled}
          onToggleSound={toggleSound}
        />
      )}
    </div>
  );
};

export default NotificationCenter;
